import { AraClient, streamAra } from './client';
import type { AraClientInit, AraHandlers, AraStreamOptions, StartParams } from './types';
import { initPopup, initFull, type InitOptions, type Theme } from './ui';
import { defineAraFullChat } from './web-component';

export { AraClient, streamAra, initPopup as init, initFull, defineAraFullChat };
export type { AraClientInit, AraHandlers, AraStreamOptions, StartParams };
export type { InitOptions, Theme };

// Minimal DOM widget (optional)
export type WidgetOptions = {
  container?: HTMLElement | string;
  client: AraClient;
  chatId: string;
  locale?: string;
  placeholder?: string;
  requestType?: string;
  walletAddress?: string;
};

export function createAraWidget(opts: WidgetOptions) {
  const container =
    typeof opts.container === 'string'
      ? (document.querySelector(opts.container) as HTMLElement | null)
      : opts.container ?? document.body;
  if (!container) throw new Error('Container not found');

  const root = document.createElement('div');
  root.style.position = 'relative';
  root.style.display = 'flex';
  root.style.flexDirection = 'column';
  root.style.border = '1px solid #e5e7eb';
  root.style.borderRadius = '12px';
  root.style.padding = '12px';
  root.style.width = '320px';
  root.style.height = '420px';
  root.style.boxShadow = '0 10px 25px rgba(0,0,0,0.08)';
  root.style.background = '#fff';

  const view = document.createElement('div');
  view.style.flex = '1';
  view.style.overflow = 'auto';
  view.style.fontFamily = 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial';
  view.style.fontSize = '14px';
  view.style.lineHeight = '1.5';
  view.style.color = '#111827';

  const inputWrap = document.createElement('div');
  inputWrap.style.display = 'flex';
  inputWrap.style.gap = '8px';

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = opts.placeholder ?? 'Ask ARA...';
  input.style.flex = '1';
  input.style.border = '1px solid #e5e7eb';
  input.style.borderRadius = '8px';
  input.style.padding = '8px 10px';

  const send = document.createElement('button');
  send.textContent = 'Send';
  send.type = 'button';
  send.style.border = '1px solid #e5e7eb';
  send.style.borderRadius = '8px';
  send.style.padding = '8px 12px';
  send.style.background = '#111827';
  send.style.color = '#fff';

  inputWrap.appendChild(input);
  inputWrap.appendChild(send);

  root.appendChild(view);
  root.appendChild(inputWrap);
  container.appendChild(root);

  let systemDiv: HTMLDivElement | null = null;
  let answerDiv: HTMLDivElement | null = null;

  function appendUser(text: string) {
    const box = document.createElement('div');
    box.style.margin = '8px 0';
    box.style.textAlign = 'right';
    box.innerHTML = `<span style="display:inline-block;background:#111827;color:#fff;padding:6px 10px;border-radius:10px 10px 2px 10px;max-width:80%">${escapeHtml(
      text
    )}</span>`;
    view.appendChild(box);
    view.scrollTop = view.scrollHeight;
  }

  function begin(meta: Record<string, unknown>) {
    systemDiv = document.createElement('div');
    systemDiv.style.margin = '8px 0';
    systemDiv.style.color = '#6b7280';
    systemDiv.textContent = '[ARA connected]';
    view.appendChild(systemDiv);

    answerDiv = document.createElement('div');
    answerDiv.style.margin = '8px 0';
    answerDiv.style.textAlign = 'left';
    answerDiv.innerHTML = `<span style="display:inline-block;background:#f3f4f6;color:#111827;padding:6px 10px;border-radius:10px 10px 10px 2px;max-width:80%"></span>`;
    view.appendChild(answerDiv);
    view.scrollTop = view.scrollHeight;
  }

  function item(text: string) {
    if (!answerDiv) return;
    const span = answerDiv.querySelector('span');
    if (!span) return;
    span.textContent = (span.textContent ?? '') + text;
    view.scrollTop = view.scrollHeight;
  }

  function end() {
    systemDiv = null;
    answerDiv = null;
  }

  function error(err: unknown) {
    const box = document.createElement('div');
    box.style.margin = '8px 0';
    box.style.color = '#ef4444';
    box.textContent = `Error: ${String(err)}`;
    view.appendChild(box);
  }

  async function sendMessage() {
    const q = input.value.trim();
    if (!q) return;
    appendUser(q);
    input.value = '';
    await opts.client.start(
      {
        body: {
          chatId: opts.chatId,
          question: q,
          locale: opts.locale ?? (navigator.language || 'en-US'),
          requestType: opts.requestType,
          walletAddress: opts.walletAddress,
        },
      },
      { onBegin: begin, onItem: (t) => item(t), onEnd: end, onError: error }
    );
  }

  send.addEventListener('click', sendMessage);
  let composing = false;
  input.addEventListener('compositionstart', () => {
    composing = true;
  });
  input.addEventListener('compositionend', () => {
    composing = false;
  });
  input.addEventListener('keydown', (e) => {
    // IME 조합(한글 등) 중 Enter 입력은 무시하여 중복 전송 방지
    // 일부 브라우저는 조합 중 keyCode 229를 사용
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyEvent = e as any;
    if (composing || (e as KeyboardEvent).isComposing || anyEvent.keyCode === 229) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      sendMessage();
    }
  });

  return {
    destroy() {
      opts.client.cancel();
      root.remove();
    },
  } as const;
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

declare global {
  interface Window {
    AraEmbed?: {
      AraClient: typeof AraClient;
      createAraWidget: typeof createAraWidget;
      init: typeof initPopup;
      initFull: typeof initFull;
      defineAraFullChat: typeof defineAraFullChat;
    };
  }
}

// UMD-style global for <script> usage when built as IIFE
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).AraEmbed = { AraClient, createAraWidget, init: initPopup, initFull, defineAraFullChat };
  try {
    defineAraFullChat();
  } catch {}
}


